/**
 * RequireStage Wrapper
 *
 * Wraps page content with a LockedScreenOverlay when the user's subscription
 * stage is below the required minimum. The underlying content still renders
 * (blurred) so users can see what they're missing.
 */

import { useSubscriptionStore, type FoundryStage } from '@/stores/subscription';
import { LockedScreenOverlay } from './LockedScreenOverlay';

interface RequireStageProps {
  /** Minimum stage required to access this content */
  stage: FoundryStage;
  /** Feature name for the overlay */
  featureName: string;
  /** Description for the overlay */
  description: string;
  /** Capabilities list for the overlay */
  capabilities?: string[];
  /** The page content (renders blurred if locked) */
  children: React.ReactNode;
}

export function RequireStage({
  stage,
  featureName,
  description,
  capabilities = [],
  children,
}: RequireStageProps) {
  const hasAccess = useSubscriptionStore((s) => s.hasAccess);

  if (hasAccess(stage)) {
    return <>{children}</>;
  }

  return (
    <div className="relative h-screen overflow-hidden">
      {/* Render children behind the overlay (clipped to viewport, blurred) */}
      <div className="pointer-events-none select-none h-full overflow-hidden" aria-hidden="true">
        {children}
      </div>
      <LockedScreenOverlay
        featureName={featureName}
        description={description}
        requiredStage={stage}
        capabilities={capabilities}
      />
    </div>
  );
}
